import register from 'preact-custom-element'
import { useEffect, useState, useRef } from "preact/hooks"
import { formatMoney } from '@/scripts/formatMoney'
import { disablePageScroll, enablePageScroll } from 'scroll-lock'
import debounce from 'lodash/debounce'

/**
 * Call GraphQL Shopify Storefront API
 */
async function storefrontClient(query, variables = {}, version = '2022-07') {
  const endpoint = `https://${window.BARREL.shop}/api/${version}/graphql.json`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Shopify-Storefront-Access-Token': window.BARREL.storefrontAccessToken,
    },
    body: JSON.stringify({ query, variables }),
  });
  return await response.json();
}

/**
 * Get metadata variants / free_gift / bundle_components
 */
async function fetchProductDetails(id) {
  try {
    const query = `query {
      product(id:"gid://shopify/Product/${id}") {
        bundle_components_meta: metafield(namespace:"accentuate", key:"bundle_components") { value }
        free_gift_meta:    metafield(namespace:"plp_filter", key:"free_gift")     { value }
        variants(first:10) { nodes { id availableForSale } }
      }
    }`;

    const result = await storefrontClient(query);

    if (!result?.data?.product) {
      console.error(`fetchProductDetails: missing product data for id ${id}`, result);
      return { bundle_components_meta: null, free_gift_meta: null, variants: [] };
    }

    const product = result.data.product;
    return {
      bundle_components_meta: product.bundle_components_meta?.value || null,
      free_gift_meta:         product.free_gift_meta?.value   || null,
      variants:               product.variants?.nodes         || [],
    };
  } catch (err) {
    console.error('fetchProductDetails error', err);
    return { bundle_components_meta: null, free_gift_meta: null, variants: [] };
  }
}

/**
 * Handle ATC (free gift & bundle)
 */
async function loadFreeProduct(mainProduct, freeGiftSKU) {
  // Get variant of free gift
  const res = await storefrontClient(`{
    product(handle:"${freeGiftSKU}") {
      variants(first:1){ nodes { id availableForSale } }
    }
  }`);
  const freeVar = res.data.product.variants.nodes.find(v => v.availableForSale);
  const freeId = freeVar?.id.split('/').pop();

  // Get main variant
  const mainVar = mainProduct.variants.find(v => v.availableForSale);
  const mainId = mainVar?.id.split('/').pop();

  const items = [];
  if (mainId) items.push({ id: mainId, quantity: 1 });
  if (freeId) items.push({ id: freeId, quantity: 1 });

  document.querySelector('shopify-cart').addItem({
    items,
    sections: 'site-header,cart',
    sections_url: window.location.pathname,
  });
}

const CompareProducts = () => {
    const [showModal, setShowModal] = useState(false)
    const [showDetails, setShowDetails] = useState(false)
    const [products, setProducts] = useState([])
    const [emptyBox, setEmptyBox] = useState([])
    const listBlock = useRef(null)
    const arrowIcon = useRef(null)
    const listContent = useRef(null)
    const detailContent = useRef(null)
    const stickyProducts = useRef(null)
    const stickyProductsContent = useRef(null)
    const detailContentCard = useRef(null)
    const header = useRef(null)
    const detailContentInfo = useRef(null)

    const optionClass = 'h5 lg:h4 w-full md:w-auto md:min-w-[252px] px-[16px] md:px-[20px] py-[4px] md:py-[24px] bg-white capitalize'
    const optionOldClass = 'h5 lg:h4 w-full md:w-auto md:min-w-[252px] px-[16px] md:px-[20px] py-[4px] md:py-[24px] bg-white md:bg-grey'
    const tableClass = 'relative flex justify-start items-center w-[175px] md:w-[273px] first:pl-[ min-w-[175px] min-h-[46px] md:min-w-[273px] px-[6px] md:px-[20px] first:pl-[16px] md:first:pl-[20px] py-4 md:py-[24px] border-l bg-grey md:bg-white'
    const tableOldClass = 'relative flex justify-start items-center w-[175px] md:w-[273px] min-w-[175px] min-h-[46px] md:min-w-[273px] px-[6px] md:px-[20px] first:pl-[16px] md:first:pl-[20px] py-4 md:py-[24px] border-l bg-grey'
    const classCTA = 'btn-primary w-full px-4 !m-0 h-[45px] z-[2]'
    const removeChecked = (id) => {
        const idInput = 'compare-product-' + id
        const checkboxEl = document.getElementById(idInput)
        if (checkboxEl) {
            checkboxEl.checked = false
        }
    }

    const removeProduct = (id) => {
        setProducts((prevProducts) => {
            const updatedProducts = prevProducts.filter(product => product.id !== id)
            localStorage.setItem('compareProducts', JSON.stringify(updatedProducts))
            return updatedProducts
        })

        removeChecked(id)
    }

    const compareProduct = () => {
        const eventData = {
          event: "product_comparison_click",
          compare_count: products.length,
          page_location: window.location.href,
          products: products.map((p) => ({
            item_id: p.id,
            item_name: p.title
          }))
        };
        console.log("[GA4] product_comparison_click:", eventData);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(eventData);

        setShowDetails(true)
        disablePageScroll()
    }

    const closeDetails = () => {
        setShowDetails(false)
        enablePageScroll()
    }

    const clearAll = () => {
        products.forEach(product => {
            removeChecked(product.id)
        })

        setProducts([])
        localStorage.setItem('compareProducts', JSON.stringify([]))
    }

    const toggle = () => {
        if (!arrowIcon.current.classList.contains("show")) {
            arrowIcon.current.classList.add("show")
            listContent.current.style.height = listContent.current.scrollHeight + "px"
        } else {
            arrowIcon.current.classList.remove("show")
            listContent.current.style.height = "0"
        }
    }

    const productBadges = (hit, extraClass = '') => {
        let { tags, price, compare_at_price: comparePrice } = hit
        const availableForSale = true
        const isBundle = false

        const handleize = (string) =>
            string
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/-$/, "")
                .replace(/^-/, "")

        let badges = []
        let uniqueBadges = []
        let max = 2
        if (comparePrice > price) {
            badges.push("Sale")
        }
        const labelClass =
            "mr-1 mb-1 p-1 p2 font-bold product-card__badge bg-blue text-white"
        const sortRules =
            "badge:Best Seller,badge:New,badge:Gift Guide,badge:Available on B+D,badge:,badge_custom:"
        const sortRulesArr = sortRules.split(",")

        if (tags && tags.length > 0) {
            sortRulesArr.forEach((rule) => {
                if (tags.includes(rule)) {
                    badges.push(rule.replace("badge:", ""))
                }
                if (rule === "badge:") {
                    const listBadge = tags.filter(
                        (tag) => !sortRulesArr.includes(tag) && tag.includes("badge:")
                    )
                    if (listBadge.length) {
                        badges = badges.concat(
                            listBadge.map((tag) => tag.replace("badge:", ""))
                        )
                    }
                }

                if (rule === "badge_custom:") {
                    const listBadgeCustom = tags.filter(
                        (tag) => !sortRulesArr.includes(tag) && tag.includes("badge_custom:")
                    )
                    if (listBadgeCustom.length) {
                        badges = badges.concat(
                            listBadgeCustom.map((tag) => tag.replace("badge_custom:", ""))
                        )
                    }
                }
            })
        }

        if (badges.length > max) {
            badges = badges.slice(0, max);
        }

        let hideSaleTag = false;
        if (
            tags &&
            tags.includes("available:online") &&
            !tags.includes("D2C-Restriction")
        ) {
            if (availableForSale || isBundle) {
                hideSaleTag = false;
            } else {
                hideSaleTag = true;
            }
        } else {
            hideSaleTag = true;
        }
        uniqueBadges = [...new Set(badges)]
        badges = uniqueBadges;

        if (hideSaleTag) {
            badges = badges.filter((e) => e !== "Sale")
        }

        if (!badges.length) return ""


        return `<div
          class="product-card__badges empty:hidden mt-2 mb-1 flex flex-wrap h-[25px] lg:h-[27px] ${extraClass}"
        >
          ${badges.map((tag) => {
            return `<div class="flex">
              <span class="badge ${labelClass} bg-${handleize(tag)}">${tag}</span>
            </div>`
        }).join('')}
        </div>`
    }

    const shouldDisplayProductPrice = (product, isBundle) => {
        // Check if the product has the required tag and doesn't contain restricted tags.
        const hasOnlineTag = product.tags.includes("available:online");
        const hasRestrictedTags = product.tags.includes("MAPP") || product.tags.includes("D2C-Restriction");

        // Check if the product is available or it's a bundle.
        const isProductAvailable = product.can_order || isBundle || product.inventory_available;

        // Determine whether to render the price card.
        return hasOnlineTag && !hasRestrictedTags && isProductAvailable;
    }


    const renderProductPriceCard = (product) => {
        const isBundle = product.product_type === "Bundle";

        if (shouldDisplayProductPrice(product, isBundle)) {
            const regularPriceLabel = "Regular Price";
            const salePriceLabel = "Sale Price";
            const isOnSale = product.compare_at_price > product.price;

            const price = formatMoney(product.price * 100);
            const compareAtPrice = formatMoney(product.compare_at_price * 100);

            return (
                <div
                    regular-price-label={regularPriceLabel}
                    sale-price-label={salePriceLabel}
                    price={price}
                    compare-at-price={compareAtPrice}
                >
                    {isOnSale ? (
                        <div>
                            <span className="sr-only">{regularPriceLabel}</span>
                            <s className="mr-1">{compareAtPrice}</s>
                            <span className="sr-only">{salePriceLabel}</span>
                            <strong>{price}</strong>
                        </div>
                    ) : (
                        <span>{price}</span>
                    )}
                </div>
            );
        }
        return null;
    }

    const ratingClass = (hit) => {
        return `mt-1.5 bv-rating--hide-number ${hit?.meta?.BazaarVoice?.Rating <= 0 ? "bv-rating-custom-hide-zero" : ""}`
    }

    const exerptText = (text, length) => {
        return text && text.length > length ? text.substring(0, length) + '...' : ''
    }

    const ProductDescription = ({ product }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const [description, setDescription] = useState('');

        const toggleDescription = () => {
            setIsExpanded(!isExpanded);
        };

        useEffect(() => {
            const fetchDescription = async () => {
                try {
                    const response = await fetch(
                        `/products/${JSON.parse(product.hit)?.handle}?view=meta-description`
                    );
                    if (!response.ok) {
                        throw new Error(`Error fetching description: ${response.statusText}`);
                    }
                    const descriptionText = await response.text();
                    setDescription(descriptionText || '');
                } catch (error) {
                    console.error(error);
                    setDescription('');
                }
            };

            fetchDescription();
        }, [product]);

        if (!description) return null;

        return (
            <div key={product.id + 'description'} className={tableClass}>
                <div className='p1'>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: description.length > 85 ? isExpanded ? description : exerptText(description, 85) : description,
                        }}
                    />
                    {description.length > 85 && (
                        <button onClick={toggleDescription} className='link-underline mt-2'>
                            {isExpanded ? 'See less' : 'See more'}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const handleCompareChartClick = (product) => {
        const productData = {
          event: "select_item",
          ecommerce: {
            item_list_name: "Compare Chart",
            items: [
              {
                item_id: product.id,
                item_name: product.title,
                item_category: JSON.parse(product.hit)?.product_type || "",
                item_brand: JSON.parse(product.hit)?.vendor || ""
              }
            ]
          }
        };
        console.log("[GA4] select_item (compare chart click):", productData);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(productData);
    };

    const hasDataProductType = products.some(product =>
        JSON.parse(product.hit)?.product_type
    );

    const scrollHandle = debounce(() => {

    }, 200, { leading: false, trailing: true })

    useEffect(() => {
        const handleCompareProduct = () => {
            const currentProducts = localStorage.getItem('compareProducts')
            const data = JSON.parse(currentProducts) || []
            setProducts(data)
            setShowModal(data.length > 0)
        }

        window.addEventListener('compare-product', handleCompareProduct)
    }, [])

    useEffect(() => {
        setEmptyBox(Array.from({ length: (4 - products.length) }, (_, i) => i + 1))
    }, [products])

    /**
     * Component button Add to Cart / View Details
     */
    const LoadCTAButton = ({ productData }) => {
        const hit = JSON.parse(productData.hit);
        const {
            tags = [],
            handle,
            id,
            available: availableShopify = false,
            can_order: canOrder = false,
            product_type: productType,
        } = hit;

        const [variants, setVariants] = useState([]);
        const [bundleComponents, setBundleComponents] = useState([]);
        const [freeGiftMeta, setFreeGiftMeta] = useState(null);

        useEffect(() => {
            (async () => {
                const { bundle_components_meta, free_gift_meta, variants: vs } =
                    await fetchProductDetails(id);
                setVariants(vs);
                setFreeGiftMeta(free_gift_meta);

            })();
        }, [id]);

        const onAdd = async (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            const pd = { ...hit, variants, free_gift: freeGiftMeta };

            if (pd.free_gift) {
                await loadFreeProduct(pd, pd.free_gift);
                return;
            }

            const variantAvailable = pd.variants.find(v => v.availableForSale);
            const variantId = variantAvailable
                ? variantAvailable.id.split('/').pop()
                : pd.objectID;

            const items = (pd.product_type === 'Bundle' && bundleComponents.length)
                ? bundleComponents.map(cmp => ({
                    id: cmp.id.split('/').pop(),
                    quantity: 1,
                    properties: {
                        _bundle_product_id:    pd.id.toString(),
                        _bundle_product_title: pd.title,
                        _bundle_product_image: `${pd.product_image}&width=134`,
                        _bundle_product_url:   `/products/${pd.handle}`,
                    }
                }))
                : [{ id: variantId, quantity: 1 }];

            await document
                .querySelector('shopify-cart')
                .addItem({
                    items,
                    sections: 'site-header,cart',
                    sections_url: window.location.pathname,
                });
            closeDetails()
        };

        if (canOrder && productType !== 'Bundle' && tags.includes("available:online")) {
            return <button onClick={onAdd} className={classCTA}>Add to Cart</button>;
        }
        return <a href={`/products/${handle}`} className={classCTA}>Learn More</a>;
    };

    const getWarrantyText = (product) => {
      const code = JSON.parse(product.hit)?.meta?.Salsify?.WARRANTY;
      const allowedCodes = window.brCompareData?.warrantyCodesInclude || [];
      const codeMap = window.brCompareData?.warrantyCodes || {};

      if (!code || !allowedCodes.includes(code)) {
        return '';
      }

      return codeMap[code] || '';
    };

    return (
        <>
            {showModal && products.length > 0 && (
                <div className='w-full max-w-[1120px] mx-auto py-[16px] px-[10px] md:p-[32px] fixed left-0 right-0 bottom-0 bg-white shadow-compare'>
                    <div className='max-w-[1121px] m-auto'>
                        <div className='relative' ref={listBlock}>
                            <h2 className='sr-only'>Product Comparison ({products?.length || 0}/4)</h2>
                            <div className='relative flex flex-row justify-between items-center md:hidden'>
                                <div className='h3 pl-[6px]'>Product Comparison ({products?.length || 0}/4)</div>
                                <button className='w-[12px] h-[12px]' onClick={toggle} ref={arrowIcon}>
                                    <span className='absolute inset-0'></span>
                                    <div className='[.show_&]:rotate-180 transition-[rotate]'>
                                        <svg width="12" height="6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m1 1.164 4.891 3.672 4.891-3.672" stroke="#000" /></svg>
                                    </div>
                                </button>
                            </div>
                            <div className='overflow-hidden transition-height duration-300 h-0 md:h-auto' ref={listContent}>
                                <div className='pt-[22px] mb-[1px] md:mb-0 md:pt-0 flex flex-col md:flex-row items-center justify-center flex-wrap md:-mx-[21px] gap-[10px] md:gap-0 animate-fade-in'>
                                    {products.map((product) => (
                                        <div key={product.id} className='relative flex gap-[12px] w-full lg:h-full lg:min-h-[94px] md:w-1/2 lg:w-1/4 md:mb-[10px] lg:mb-0 md:px-[21px] fade-in' style={{ transition: 'opacity 0.3s' }}>
                                            <div className='w-[40px] h-[40px] md:w-[48px] md:h-[48px] min-w-[40px] md:min-w-[48px]'>
                                                <img src={product.img} alt={product.title} loading="lazy" />
                                            </div>

                                            <h3 className='h5 grow'>{product.title}</h3>

                                            <button onClick={() => removeProduct(product.id)} className='w-[18px] h-[18px] min-w-[18px]'>
                                                <svg width="19" height="19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="9.039" cy="9.039" r="9.039" fill="#D9D9D9" />
                                                    <path d="m6.693 6.704 4.67 4.67M11.363 6.704l-4.67 4.67" stroke="#000" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}

                                    {emptyBox.map(() => (
                                        <div className='w-full md:w-1/2 lg:w-1/4 md:px-[21px] md:mb-[10px] lg:mb-0 fade-in' style={{ transition: 'opacity 0.3s' }}>
                                            <div className='p1 py-[11px] md:py-[14px] px-[45px] text-charcoal border border-grey-7 text-center'>Add Product</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className='flex gap-[20px] justify-between items-center mt-[16px] md:mt-[32px]'>
                            <div className='w-[50%] h4 hidden md:block'>Product Comparison ({products?.length || 0}/4)</div>
                            <div className='flex flex-col md:flex-row w-full md:w-[50%] gap-[16px] justify-end items-center' >
                                <button onClick={compareProduct} className='btn-primary cursor-pointer w-full max-w-[233px] px-4 !m-0 h-[45px]'>Compare Products</button>
                                <button onClick={() => clearAll()} className='h5 underline'>Clear All</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showDetails && products.length > 0 && (
                <div className='fixed overflow-auto top-0 left-0 w-full h-screen bg-white z-[1]'>
                    <div className='pt-[56px] px-[16px] pb-[32px] lg:px-[48px] xl:px-[56px] border-b max-w-[1440px] mx-auto' ref={header}>
                        <div className='flex justify-between items-start'>
                            <div className='flex flex-col max-w-[80%] gap-[8px] lg:gap-[12px]'>
                                <h2 className='h2'>{ window.brCompareData?.title }</h2>
                                <p className='p1'>{ window.brCompareData?.description?.slice(0, parseInt(window.brCompareData?.maxLength)) }</p>
                            </div>

                            <button className='w-[38px] h-[38px]' onClick={closeDetails}>
                                <svg viewBox='0 0 39 39' fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="19.86" cy="19.637" r="19.14" fill="#EAEAEA" /><path d="m14.892 14.694 9.889 9.888M24.78 14.694l-9.888 9.888" stroke="#000" /></svg>
                            </button>
                        </div>
                    </div>
                    <div className='fixed w-full left-0 top-0 lg:pl-[48px] xl:pl-[56px] bg-white z-10 opacity-0 invisible overflow-auto h-screen shadow-compare-bottom transition-all' ref={stickyProducts}>
                        <div className='w-auto max-w-[1440px] mx-auto py-[16px] lg:py-[32px] pl-[16px] md:pl-[252px] pr-[16px] md:pr-[56px] overflow-x-auto' data-scroll-lock-scrollable ref={stickyProductsContent}>
                            <div className='w-auto flex -md-px-[6px] md:-px-[20px]'>
                                {products.map((product) => (
                                    <div key={product.id} className='relative flex gap-[8px] md:gap-[12px] w-[175px] md:w-[273px] min-w-[175px] md:min-w-[273px] px-[6px] md:px-[20px]'>
                                        <a
                                          className='absolute inset-0 w-full h-full z-10'
                                          href={product.url}
                                          aria-label={product.title}
                                          onClick={() => handleCompareChartClick(product)}
                                        ></a>
                                        <div className='w-[40px] h-[40px] md:w-[48px] md:h-[48px] min-w-[40px] md:min-w-[48px]'>
                                            <img src={product.img} alt={product.title} loading="lazy" />
                                        </div>

                                        <h3 className='h5 grow'>{product.title}</h3>

                                        <button onClick={() => removeProduct(product.id)} className='w-[18px] h-[18px] min-w-[18px]'>
                                            <svg width="19" height="19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="9.039" cy="9.039" r="9.039" fill="#D9D9D9" />
                                                <path d="m6.693 6.704 4.67 4.67M11.363 6.704l-4.67 4.67" stroke="#000" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                                {emptyBox.map(() => (
                                    <div className='w-[175px] md:w-[273px] min-w-[175px] md:min-w-[273px] px-[6px] md:px-[20px] flex items-center'>
                                        <div className='p1 py-[11px] md:py-[14px] px-[45px] text-charcoal border border-grey-7 text-center'>Add Product</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className='relative w-full lg:pl-[48px] xl:pl-[56px] max-w-[1440px] mx-auto overflow-auto' data-scroll-lock-scrollable ref={detailContent}>
                        <div className='w-auto md:pl-[252px] mt-[32px] pr-[16px] md:pr-[56px] pb-[20px] md:pb-[40px]' ref={detailContentCard}>
                            <div className='w-fit flex -md-px-[6px] md:-px-[20px]'>
                                {products.map((product) => (
                                    <div key={product.id} className='relative flex flex-col w-[175px] md:w-[273px] min-w-[175px] md:min-w-[273px] first:pl-4 px-[6px] md:px-[20px]'>
                                        <a href={product.url} className='absolute inset-0 z-[1]'></a>
                                        <div className='w-full'>
                                            <img src={product.img} alt={product.title} loading="lazy" />
                                        </div>
                                        <div className='relative grow mb-8 lg:mb-10'>
                                            {JSON.parse(product.hit).tags && JSON.parse(product.hit).tags.length > 0 && (
                                                <div className='static empty:mb-8 empty:lg:mb-10' dangerouslySetInnerHTML={{ __html: productBadges(JSON.parse(product.hit), '!h-auto') }} />
                                            )}
                                            <h3 className='h5'>{product.title}</h3>
                                        </div>
                                        <LoadCTAButton productData={product} />

                                        <button onClick={() => removeProduct(product.id)} className=' absolute top-[8px] right-[8px] w-[23px] z-10 md:w-[38px] h-[23px] md:h-[38px]'>
                                            <svg viewBox='0 0 39 39' fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="19.86" cy="19.637" r="19.14" fill="#EAEAEA" /><path d="m14.892 14.694 9.889 9.888M24.78 14.694l-9.888 9.888" stroke="#000" /></svg>
                                        </button>
                                    </div>
                                ))}

                                {emptyBox.map(() => (
                                    <div className='w-[175px] md:w-[273px] min-w-[175px] md:min-w-[273px] px-[6px] md:px-[20px] flex items-end'>
                                        <button onClick={closeDetails} className='btn-primary w-full max-w-[233px] px-4 !m-0 h-[45px]'>Add Product</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className='group' ref={detailContentInfo}>
                            <div className='w-full flex flex-col md:flex-row'>
                                <div className={optionOldClass}>SKU</div>
                                <div className='w-full md:w-auto flex md:flex-1'>
                                    {products.map((product) => (
                                        <div key={product.id + 'sku'} className={tableOldClass}>
                                            <div className='p1' dangerouslySetInnerHTML={{ __html: JSON.parse(product.hit)?.sku }} />
                                        </div>
                                    ))}

                                    {emptyBox.map(() => (
                                        <div className={tableOldClass}>
                                            -
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className='w-full flex flex-col md:flex-row'>
                                <div className={optionClass}>Price</div>
                                <div className='w-full md:w-auto flex md:flex-1'>
                                    {products.map((product) => (
                                        <div key={product.id + 'price'} className={tableClass}>
                                            <div className='p1'>
                                                {renderProductPriceCard(JSON.parse(product.hit))}
                                            </div>
                                        </div>
                                    ))}

                                    {emptyBox.map(() => (
                                        <div className={tableClass}>
                                            -
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className='w-full flex flex-col md:flex-row'>
                                <div className={optionOldClass}>Rating</div>
                                <div className='w-full md:w-auto flex md:flex-1'>
                                    {products.map((product) => (
                                        <div key={product.id + 'rating'} className={tableOldClass}>
                                            <div
                                                className={ratingClass(JSON.parse(product.hit))}
                                                data-bv-show="inline_rating"
                                                data-bv-product-id={product.id}
                                                data-bv-redirect-url={product.url}
                                                data-bv-seo="false"
                                            ></div>
                                        </div>
                                    ))}

                                    {emptyBox.map(() => (
                                        <div className={tableOldClass}>
                                            -
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className='w-full flex flex-col md:flex-row'>
                                <div className={optionClass}>Description</div>
                                <div className='w-full md:w-auto flex md:flex-1'>
                                    {products.map((product) => (
                                        <ProductDescription key={product.id + 'desc'} product={product} />
                                    ))}

                                    {emptyBox.map((_, index) => (
                                        <div key={index} className={tableClass}>
                                            -
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {hasDataProductType && (
                                <div className='w-full flex flex-col md:flex-row'>
                                    <div className={optionOldClass}>Product Type</div>
                                    <div className='w-full md:w-auto flex md:flex-1'>
                                        {products.map((product) => (
                                            <div key={product.id + 'type'} className={tableOldClass}>
                                                <div className='p1' dangerouslySetInnerHTML={{ __html: JSON.parse(product.hit)?.product_type }} />
                                            </div>
                                        ))}

                                        {emptyBox.map(() => (
                                            <div className={tableOldClass}>
                                                -
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

							{window.brCompareData?.types && window.brCompareData?.types.length > 0 && window.brCompareData?.types.map((type, index) => {
                                const currentClass = index % 2 === 0 ? tableClass : tableOldClass;
                                const currentOptionClass = index % 2 === 0 ? optionClass : optionOldClass;

                                const hasData = products.some(product =>
                                    JSON.parse(product.hit)?.meta?.Salsify && JSON.parse(product.hit)?.meta?.Salsify[type.key]
                                );

                                return hasData ? (
                                    <div className='w-full flex flex-col md:flex-row'>
                                        <div className={currentOptionClass} dangerouslySetInnerHTML={{ __html: type.title }} />
                                        <div className='w-full md:w-auto flex md:flex-1'>
                                            {products.map((product) => (
                                                <div key={product.id + 'description'} className={currentClass}>
                                                <div className='p1' dangerouslySetInnerHTML={{
                                                    __html:
                                                      type.key === 'WARRANTY'
                                                        ? (getWarrantyText(product) || '-')
                                                        : (JSON.parse(product.hit)?.meta?.Salsify?.[type.key] || '-')
                                                  }} />
                                                </div>
                                            ))}

                                            {emptyBox.map(() => (
                                                <div className={currentClass}>
                                                    -
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

if (!customElements.get('compare-products')) {
    register(CompareProducts, 'compare-products');
}
